import { Card, Col, Row, Skeleton, Space, Statistic, Tag, Typography } from 'antd';
import {
  CalendarOutlined,
  EditOutlined,
  ReadOutlined,
  SafetyCertificateOutlined,
  CustomerServiceOutlined,
  RollbackOutlined,
} from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { getCommunityStats } from '../api/community';
import { listFeedback } from '../api/feedback';
import { listRefunds } from '../api/admin';
import { fetchAnalyticsOverview } from '../api/analytics';
import { ErrorAlert } from '../components/ErrorAlert';
import { useAuth } from '../auth/AuthProvider';
import {
  useCanHandleFeedback,
  useCanHandleRefunds,
} from '../auth/usePermissions';
import { formatShanghai } from '../utils/datetime';
import { LibraryPendingCard } from './library/LibraryPendingCard';
import { TodoStatCard } from './dashboard/TodoStatCard';

const ROLE_LABELS: Record<string, string> = {
  SUPER_ADMIN: '超级管理员',
  CONTENT_EDITOR: '内容运营',
  ACTIVITY_OPERATOR: '活动运营',
  MODERATOR: '社区审核',
  CUSTOMER_SERVICE: '客服',
};

/** 后端 /community/stats、/analytics/overview 的角色白名单（AdminRoles 注解） */
const COMMUNITY_TODO_ROLES = ['SUPER_ADMIN', 'MODERATOR'];
const ANALYTICS_ROLES = ['SUPER_ADMIN', 'CONTENT_EDITOR', 'ACTIVITY_OPERATOR'];

function greetingByHour(date: Date): string {
  const hour = Number(
    new Intl.DateTimeFormat('zh-CN', {
      timeZone: 'Asia/Shanghai',
      hour: 'numeric',
      hour12: false,
    }).format(date),
  );
  if (hour < 6) {
    return '夜深了';
  }
  if (hour < 12) {
    return '早上好';
  }
  if (hour < 14) {
    return '中午好';
  }
  if (hour < 18) {
    return '下午好';
  }
  return '晚上好';
}

/**
 * 概览页「社区审核」待办块：对接 /community/stats 聚合端点（单次请求）。
 * 角色与后端 AdminCommunityController 的 @AdminRoles('MODERATOR') 一致
 * （AdminRolesGuard 对 SUPER_ADMIN 直接放行）；其余角色整块隐藏，不发请求。
 */
function CommunityTodoSection(): React.ReactElement | null {
  const { admin } = useAuth();
  const allowed = admin?.roles.some((role) => COMMUNITY_TODO_ROLES.includes(role)) ?? false;

  const query = useQuery({
    queryKey: ['community-stats'],
    queryFn: getCommunityStats,
    enabled: allowed,
    staleTime: 60_000,
  });

  if (!allowed) {
    return null;
  }

  return (
    <Space orientation="vertical" size={12} style={{ width: '100%' }}>
      {query.isError ? (
        <ErrorAlert error={query.error} onRetry={() => void query.refetch()} />
      ) : query.isPending ? (
        <Skeleton active paragraph={{ rows: 1 }} />
      ) : (
        <Space size={12} wrap>
          <TodoStatCard title="待审帖子" value={query.data?.pendingPosts ?? 0} to="/community/moderation" />
          <TodoStatCard
            title="高风险待审"
            value={query.data?.highRiskPendingPosts ?? 0}
            to="/community/moderation"
            danger
          />
          <TodoStatCard title="待审评论" value={query.data?.pendingComments ?? 0} to="/community/moderation" />
          <TodoStatCard title="待处理举报" value={query.data?.pendingReports ?? 0} to="/community/reports" />
        </Space>
      )}
      {!query.isError && !query.isPending && query.data?.oldestPendingAt ? (
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          最久积压于 {formatShanghai(query.data.oldestPendingAt)} · 近 7 天平均处理时长{' '}
          {Math.round(query.data.avgHandleMinutes)} 分钟（普通内容 SLA 2 小时，高风险 30 分钟）
        </Typography.Text>
      ) : null}
    </Space>
  );
}

/** 概览页「交易与反馈」待办块：退款（超管/客服）+ 新反馈（超管/客服），各一次 total 计数。 */
function TradeTodoSection(): React.ReactElement | null {
  const canHandleRefunds = useCanHandleRefunds();
  const canHandleFeedback = useCanHandleFeedback();

  const refundsQuery = useQuery({
    queryKey: ['dashboard-refunds-requested'],
    queryFn: () => listRefunds({ status: 'REQUESTED', page: 1, pageSize: 1 }),
    enabled: canHandleRefunds,
    staleTime: 60_000,
  });
  const feedbackQuery = useQuery({
    queryKey: ['dashboard-feedback-new'],
    queryFn: () => listFeedback({ status: 'NEW', page: 1, pageSize: 1 }),
    enabled: canHandleFeedback,
    staleTime: 60_000,
  });

  if (!canHandleRefunds && !canHandleFeedback) {
    return null;
  }

  return (
    <Space size={12} wrap>
      {canHandleRefunds ? (
        refundsQuery.isError ? (
          <ErrorAlert
            error={refundsQuery.error}
            onRetry={() => void refundsQuery.refetch()}
          />
        ) : (
          <TodoStatCard
            title="待处理退款"
            value={refundsQuery.data?.total ?? 0}
            to="/refunds"
          />
        )
      ) : null}
      {canHandleFeedback ? (
        feedbackQuery.isError ? (
          <ErrorAlert
            error={feedbackQuery.error}
            onRetry={() => void feedbackQuery.refetch()}
          />
        ) : (
          <TodoStatCard title="新用户反馈" value={feedbackQuery.data?.total ?? 0} to="/feedback" />
        )
      ) : null}
    </Space>
  );
}

/** 概览页「近 7 天运营速览」：复用运营数据 overview 接口，角色白名单与后端一致。 */
function AnalyticsQuickView(): React.ReactElement | null {
  const { admin } = useAuth();
  const navigate = useNavigate();
  const allowed = admin?.roles.some((role) => ANALYTICS_ROLES.includes(role)) ?? false;

  const query = useQuery({
    queryKey: ['analytics-overview', 7],
    queryFn: () => fetchAnalyticsOverview(7),
    enabled: allowed,
    staleTime: 300_000,
  });

  if (!allowed) {
    return null;
  }

  return (
    <Card
      title="近 7 天运营速览"
      variant="outlined"
      extra={<a onClick={() => navigate('/analytics')}>查看运营数据 →</a>}
    >
      {query.isError ? (
        <ErrorAlert error={query.error} onRetry={() => void query.refetch()} />
      ) : query.isPending ? (
        <Skeleton active paragraph={{ rows: 2 }} />
      ) : (
        <Row gutter={[16, 16]}>
          <Col xs={12} md={4}>
            <Statistic title="新增用户" value={query.data?.users.newUsers ?? 0} suffix="人" />
          </Col>
          <Col xs={12} md={4}>
            <Statistic title="活跃用户" value={query.data?.users.activeUsers ?? 0} suffix="人" />
          </Col>
          <Col xs={12} md={4}>
            <Statistic title="活动报名" value={query.data?.activities.registrations ?? 0} suffix="次" />
          </Col>
          <Col xs={12} md={4}>
            <Statistic title="内容浏览" value={query.data?.contents.totalViews ?? 0} suffix="次" />
          </Col>
          <Col xs={12} md={4}>
            <Statistic title="社区发帖" value={query.data?.community.publishedPosts ?? 0} suffix="条" />
          </Col>
        </Row>
      )}
    </Card>
  );
}

interface QuickAction {
  key: string;
  label: string;
  icon: React.ReactNode;
  to: string;
  allowed: boolean;
}

/** 概览页「快捷操作」：按角色能力裁剪，与后端 Guard 同一白名单。 */
function QuickActions(): React.ReactElement | null {
  const navigate = useNavigate();
  const { admin } = useAuth();
  const canHandleRefunds = useCanHandleRefunds();
  const canHandleFeedback = useCanHandleFeedback();

  const roles = admin?.roles ?? [];
  const isCommunityModerator = roles.some((role) => COMMUNITY_TODO_ROLES.includes(role));

  const actions: QuickAction[] = [
    {
      key: 'contents',
      label: '发内容',
      icon: <EditOutlined />,
      to: '/contents',
      allowed: roles.includes('SUPER_ADMIN') || roles.includes('CONTENT_EDITOR'),
    },
    {
      key: 'activities',
      label: '建活动',
      icon: <CalendarOutlined />,
      to: '/activities',
      allowed: roles.includes('SUPER_ADMIN') || roles.includes('ACTIVITY_OPERATOR'),
    },
    {
      key: 'moderation',
      label: '审社区',
      icon: <SafetyCertificateOutlined />,
      to: '/community/moderation',
      allowed: isCommunityModerator,
    },
    {
      key: 'refunds',
      label: '处理退款',
      icon: <RollbackOutlined />,
      to: '/refunds',
      allowed: canHandleRefunds,
    },
    {
      key: 'feedback',
      label: '处理反馈',
      icon: <CustomerServiceOutlined />,
      to: '/feedback',
      allowed: canHandleFeedback,
    },
    {
      key: 'library',
      label: '图书借阅',
      icon: <ReadOutlined />,
      to: '/library/borrowings',
      allowed: roles.includes('SUPER_ADMIN'),
    },
  ];

  const visible = actions.filter((action) => action.allowed);
  if (visible.length === 0) {
    return null;
  }

  return (
    <Card title="快捷操作" variant="outlined">
      <Space size={12} wrap>
        {visible.map((action) => (
          <Card
            key={action.key}
            size="small"
            hoverable
            variant="outlined"
            style={{ cursor: 'pointer', minWidth: 120, textAlign: 'center' }}
            onClick={() => navigate(action.to)}
          >
            <Space size={8}>
              {action.icon}
              <span>{action.label}</span>
            </Space>
          </Card>
        ))}
      </Space>
    </Card>
  );
}

/**
 * 概览页（概览页改造）：从「账号信息 + M0 说明 + 审计日志」
 * 重构为运营驾驶舱——欢迎条 / 今日待办（按角色裁剪）/ 近 7 天速览 / 快捷操作。
 *
 * 权限规则：各待办块与速览的角色白名单逐一对应后端 @AdminRoles 注解，
 * 无权限的块整块隐藏且不发请求（enabled=false），避免 403 弹错。
 * 审计日志请见「审计日志」菜单（含完整筛选，仅超管可见）。
 */
export function DashboardPage(): React.ReactElement {
  const { admin } = useAuth();
  const now = new Date();

  return (
    <Space orientation="vertical" size={16} style={{ width: '100%' }}>
      {/* 欢迎条：替代原「当前后台账号」大卡片（账号信息顶栏已有） */}
      <div style={{ padding: '8px 4px' }}>
        <Typography.Title level={4} style={{ marginBottom: 4 }}>
          {greetingByHour(now)}，{admin?.displayName ?? '运营同学'}
        </Typography.Title>
        <Space size={8} wrap>
          <Typography.Text type="secondary">
            {formatShanghai(now.toISOString())}
          </Typography.Text>
          {admin?.roles.map((role) => (
            <Tag color="purple" key={role} style={{ marginInlineEnd: 0 }}>
              {ROLE_LABELS[role] ?? role}
            </Tag>
          ))}
        </Space>
      </div>

      <Card title="今日待办" variant="outlined">
        <Space orientation="vertical" size={20} style={{ width: '100%' }}>
          <CommunityTodoSection />
          <TradeTodoSection />
          <LibraryPendingCard />
        </Space>
      </Card>

      <AnalyticsQuickView />

      <QuickActions />
    </Space>
  );
}
